/**
 * WindowCard — one ranked date window (spz-09-results-views).
 *
 * Renders the booking span as an outlined bar with the all-hands core span drawn as a
 * heavier filled segment *inside* it, plus the ranking drivers and per-shoulder-day
 * absence detail (households by label, never UUIDs).
 *
 * This file also carries the module's shared date helpers, base style tokens and the two
 * lines of API plumbing both result screens need. They would normally live in `src/lib/`
 * and `src/index.css`, but this module owns exactly five files (see issue #10), so the
 * lowest-level component hosts them.
 * TODO: move the date helpers to src/lib/ and the tokens into src/index.css once there is
 * a home for them that this module is allowed to touch.
 */
import { getAccessToken } from '../lib/auth'
import { FUNCTIONS_URL, SUPABASE_PUBLISHABLE_KEY } from '../lib/supabaseClient'

/**
 * A ranked date window and one absence inside it.
 *
 * TODO: import from #17 (`src/lib/windows/types.ts`, the `ranked-window` contract) once
 * it merges, and delete these stubs. Shapes are copied verbatim from that contract.
 * Note this is NOT `Window` from `src/lib/types.ts`: that is the persisted row
 * (`start_date`/`core_start_date`), this is the engine's response shape.
 */
export interface Absence {
  date: string
  household_id: string
  person_id: string | null
}

export interface RankedWindow {
  booking_start: string
  booking_end: string
  core_start: string
  core_end: string
  full_attendance_days: number
  shoulder_days: number
  week_only_ok: boolean
  rank: number
  absences: Absence[]
}

/* ----------------------------------------------------------------- the API -- */

/**
 * Headers for an edge-function call. Falls back to the publishable key when logged out so
 * the request reaches the function and comes back a clean 401 instead of failing at the
 * gateway.
 */
export async function apiHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken()
  return {
    'content-type': 'application/json',
    apikey: SUPABASE_PUBLISHABLE_KEY,
    authorization: `Bearer ${token ?? SUPABASE_PUBLISHABLE_KEY}`,
  }
}

/* ------------------------------------------------------------------ dates -- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Parse `YYYY-MM-DD` as UTC midnight so nothing shifts a day by timezone. */
export function parseISODate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return new Date(NaN)
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}

function isValid(d: Date): boolean {
  return !Number.isNaN(d.getTime())
}

function monthName(d: Date): string {
  return MONTHS[d.getUTCMonth()] ?? ''
}

/** "Jul 11" */
export function formatMonthDay(iso: string): string {
  const d = parseISODate(iso)
  if (!isValid(d)) return iso
  return `${monthName(d)} ${d.getUTCDate()}`
}

/** "Sat Jul 11" */
export function formatWeekdayMonthDay(iso: string): string {
  const d = parseISODate(iso)
  if (!isValid(d)) return iso
  return `${WEEKDAYS[d.getUTCDay()] ?? ''} ${monthName(d)} ${d.getUTCDate()}`
}

/**
 * Month-once ranges with an en-dash: "Jul 11–18", "Jul 30–Aug 2",
 * "Dec 30, 2026–Jan 2, 2027".
 */
export function formatRange(startIso: string, endIso: string): string {
  const a = parseISODate(startIso)
  const b = parseISODate(endIso)
  if (!isValid(a) || !isValid(b)) return `${startIso}–${endIso}`
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear()
  const sameMonth = sameYear && a.getUTCMonth() === b.getUTCMonth()
  if (sameMonth) return `${monthName(a)} ${a.getUTCDate()}–${b.getUTCDate()}`
  if (sameYear) {
    return `${monthName(a)} ${a.getUTCDate()}–${monthName(b)} ${b.getUTCDate()}`
  }
  return (
    `${monthName(a)} ${a.getUTCDate()}, ${a.getUTCFullYear()}` +
    `–${monthName(b)} ${b.getUTCDate()}, ${b.getUTCFullYear()}`
  )
}

/** "11th", "1st", "22nd", "23rd". */
export function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

const DAY_MS = 86_400_000

/** Inclusive day count between two ISO dates. */
export function dayCount(startIso: string, endIso: string): number {
  const a = parseISODate(startIso)
  const b = parseISODate(endIso)
  if (!isValid(a) || !isValid(b)) return 0
  return Math.round((b.getTime() - a.getTime()) / DAY_MS) + 1
}

/* ------------------------------------------------------------- base styles -- */

/**
 * Shared tokens + primitives for both result screens. Everything derives from the
 * scaffold's `--ground` / `--ink` / `--muted` / `--accent` custom properties so both
 * themes work; no state is signalled by hue alone.
 */
const BASE_CSS = `
.spz {
  --spz-surface: color-mix(in oklab, var(--ground) 93%, var(--ink) 7%);
  --spz-surface-2: color-mix(in oklab, var(--ground) 85%, var(--ink) 15%);
  --spz-line: color-mix(in oklab, var(--ink) 20%, transparent);
  --spz-line-strong: color-mix(in oklab, var(--ink) 62%, transparent);
  --spz-warn: oklch(0.52 0.15 62);
  --spz-warn-soft: color-mix(in oklab, var(--spz-warn) 16%, var(--ground));
  --spz-accent-soft: color-mix(in oklab, var(--accent) 14%, var(--ground));
  max-width: 42rem;
  margin: 0 auto;
  padding: 1.25rem 1rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-x: clip;
}
@media (prefers-color-scheme: dark) {
  .spz { --spz-warn: oklch(0.82 0.13 74); }
}
.spz-head { display: flex; flex-direction: column; gap: 0.25rem; }
.spz-head h1 { font-size: 1.5rem; letter-spacing: -0.02em; line-height: 1.2; }
.spz-head .spz-sub { color: var(--muted); font-size: 0.9rem; }
.spz-nav { display: flex; gap: 0.75rem; font-size: 0.85rem; flex-wrap: wrap; }
.spz-card {
  background: var(--spz-surface);
  border: 1px solid var(--spz-line);
  border-radius: 12px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  max-width: 100%;
}
.spz-section-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
  font-weight: 600;
}
.spz-list { display: flex; flex-direction: column; gap: 0.75rem; }
.spz-btn {
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.55rem 0.9rem;
  min-height: 2.75rem;
  border-radius: 10px;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--ground);
  cursor: pointer;
}
.spz-btn:disabled { opacity: 0.55; cursor: progress; }
.spz-btn.spz-btn-ghost { background: transparent; color: var(--ink); border-color: var(--spz-line-strong); }
.spz-btn-row { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.spz-note {
  border: 1px dashed var(--spz-line-strong);
  border-radius: 10px;
  padding: 0.75rem;
  font-size: 0.9rem;
  color: var(--muted);
  background: var(--spz-surface);
}
.spz-note strong { color: var(--ink); }
.spz-empty {
  border: 1px dashed var(--spz-line-strong);
  border-radius: 12px;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  background: var(--spz-surface);
}
.spz-empty p { font-size: 0.95rem; }
.spz-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--spz-line-strong);
  white-space: nowrap;
}
`

/**
 * Base stylesheet for both result screens. React 19 dedupes by `href`, so every screen
 * can render it unconditionally.
 */
export function SpzStyles() {
  return (
    <style href="spz-base" precedence="spz">
      {BASE_CSS}
    </style>
  )
}

/* ------------------------------------------------------------- window card -- */

const CARD_CSS = `
.spz-win-top { display: flex; align-items: baseline; gap: 0.5rem; justify-content: space-between; }
.spz-win-rank { font-size: 0.75rem; font-weight: 700; color: var(--muted); letter-spacing: 0.06em; }
.spz-win-range { font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }
.spz-bar {
  position: relative;
  height: 2.25rem;
  border: 1px solid var(--spz-line-strong);
  border-radius: 8px;
  background: var(--spz-surface-2);
  max-width: 100%;
  overflow: hidden;
}
.spz-bar-core {
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--accent);
  border-left: 2px solid var(--ink);
  border-right: 2px solid var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2px;
}
.spz-bar-core span {
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--ground);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
}
.spz-bar-ends { display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--muted); }
.spz-spans { display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.85rem; }
.spz-spans .spz-k { color: var(--muted); }
.spz-stats { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
.spz-stat-big { font-size: 2.1rem; font-weight: 800; line-height: 1; letter-spacing: -0.03em; }
.spz-stat-label { font-size: 0.75rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.06em; }
.spz-stat-sm { font-size: 1.1rem; font-weight: 700; line-height: 1; }
.spz-badge-week {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--spz-accent-soft);
}
.spz-shoulders { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.85rem; }
.spz-shoulder-day { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.spz-shoulder-day .spz-d { font-weight: 600; white-space: nowrap; }
.spz-shoulder-day .spz-m { color: var(--muted); }
`

export interface WindowCardProps {
  window: RankedWindow
  /** Resolves a household_id to its human label. Never render raw UUIDs. */
  householdLabel: (id: string) => string
}

interface DayGroup {
  date: string
  households: { label: string; people: number; whole: boolean }[]
}

/** Group absences by date, then by household, preserving date order. */
export function groupAbsences(
  absences: Absence[],
  householdLabel: (id: string) => string,
): DayGroup[] {
  const byDate = new Map<string, Map<string, { people: number; whole: boolean }>>()
  for (const a of absences) {
    let households = byDate.get(a.date)
    if (!households) {
      households = new Map()
      byDate.set(a.date, households)
    }
    const entry = households.get(a.household_id) ?? { people: 0, whole: false }
    if (a.person_id === null) entry.whole = true
    else entry.people += 1
    households.set(a.household_id, entry)
  }
  return [...byDate.entries()]
    .sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0))
    .map(([date, households]) => ({
      date,
      households: [...households.entries()].map(([id, v]) => ({
        label: householdLabel(id),
        people: v.people,
        whole: v.whole,
      })),
    }))
}

function absenceText(h: { label: string; people: number; whole: boolean }): string {
  if (h.whole || h.people === 0) return h.label
  return `${h.label} (${h.people} ${h.people === 1 ? 'person' : 'people'})`
}

export function WindowCard({ window: w, householdLabel }: WindowCardProps) {
  const totalDays = Math.max(dayCount(w.booking_start, w.booking_end), 1)
  const offset = Math.max(dayCount(w.booking_start, w.core_start) - 1, 0)
  const coreDays = Math.max(dayCount(w.core_start, w.core_end), 1)
  const left = Math.min((offset / totalDays) * 100, 100)
  const width = Math.min((coreDays / totalDays) * 100, 100 - left)
  const groups = groupAbsences(w.absences, householdLabel)

  return (
    <article className="spz-card">
      <style href="spz-window-card" precedence="spz">
        {CARD_CSS}
      </style>

      <div className="spz-win-top">
        <div className="spz-win-range">{formatRange(w.booking_start, w.booking_end)}</div>
        <div className="spz-win-rank">#{w.rank}</div>
      </div>

      <div>
        <div className="spz-bar">
          <div
            className="spz-bar-core"
            style={{ left: `${left}%`, width: `${width}%` }}
            title={`All-hands core: ${formatRange(w.core_start, w.core_end)}`}
          >
            <span>core</span>
          </div>
        </div>
        <div className="spz-bar-ends">
          <span>{formatMonthDay(w.booking_start)}</span>
          <span>{formatMonthDay(w.booking_end)}</span>
        </div>
      </div>

      <div className="spz-spans">
        <div>
          <span className="spz-k">Booking: </span>
          {formatMonthDay(w.booking_start)} &ndash; {formatMonthDay(w.booking_end)} (
          {totalDays} days)
        </div>
        <div>
          <span className="spz-k">All-hands core: </span>
          <strong>
            {formatMonthDay(w.core_start)} &ndash; {formatMonthDay(w.core_end)}
          </strong>{' '}
          ({coreDays} days)
        </div>
      </div>

      <div className="spz-stats">
        <div>
          <div className="spz-stat-big">{w.full_attendance_days}</div>
          <div className="spz-stat-label">full-attendance days</div>
        </div>
        <div>
          <div className="spz-stat-sm">{w.shoulder_days}</div>
          <div className="spz-stat-label">shoulder days</div>
        </div>
        {w.week_only_ok ? (
          <span className="spz-pill spz-badge-week">fits week-only rentals</span>
        ) : null}
      </div>

      {groups.length > 0 ? (
        <div className="spz-shoulders">
          <div className="spz-section-title">Shoulder days</div>
          {groups.map((g) => (
            <div className="spz-shoulder-day" key={g.date}>
              <span className="spz-d">{formatWeekdayMonthDay(g.date)} &mdash;</span>
              <span className="spz-m">
                missing: {g.households.map(absenceText).join(', ')}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="spz-shoulders">
          <span className="spz-m" style={{ color: 'var(--muted)' }}>
            Everyone is present every day of this booking span.
          </span>
        </div>
      )}
    </article>
  )
}

export default WindowCard
